import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { orderAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  buildOrderWhatsAppMessage,
  shareWhatsAppToNumber,
  isActionRequiredOrder,
  STATUS_LABELS,
} from '../../utils/orderWhatsAppMessage';

const mapOrderRow = (order) => ({
  id: order.id,
  orderId: order.orderId,
  customer: order.reviewerName || order.userEmail,
  product: order.productName || `Product ${order.productId}`,
  amount: order.orderAmount,
  status: order.orderStatus,
  date: order.orderDate,
  userEmail: order.userEmail,
  mediatorId: order.mediatorId,
  mediatorName: order.mediatorName,
  productId: order.productId,
  productName: order.productName,
  whatsappNumber: order.whatsappNumber,
  refundTimeline: order.refundTimeline,
  refundFormFilledDate: order.refundFormFilledDate,
  penaltyAmount: order.penaltyAmount,
  reason: order.reason,
});

const AdminActionRequired = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const fetchActionRequiredOrders = async () => {
    setLoading(true);
    try {
      const [refillRes, refundPendingRes] = await Promise.all([
        orderAPI.searchOrders({
          page: 0,
          size: 200,
          status: ['REJECTED', 'REJECT_AND_REFILL', 'ORDER_FORM_REFILL_REQUEST', 'REFUND_FORM_REFILL_REQUEST'],
          sortBy: 'creationDate',
          sortDirection: 'DESC',
        }),
        orderAPI.searchOrders({
          page: 0,
          size: 200,
          status: ['ORDER_FORM_FILLED', 'ORDER_FORM_REFILL_REQUEST'],
          sortBy: 'creationDate',
          sortDirection: 'DESC',
        }),
      ]);
      const refillOrders = (refillRes.data?.data || []).map(mapOrderRow);
      const pendingRefund = (refundPendingRes.data?.data || []).map(mapOrderRow);
      const closingSoon = pendingRefund.filter(isActionRequiredOrder);
      const merged = [...refillOrders];
      closingSoon.forEach((order) => {
        if (!merged.some((o) => o.id === order.id)) merged.push(order);
      });
      setOrders(merged.filter(isActionRequiredOrder));
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to load action-required orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActionRequiredOrders();
  }, []);

  const handleWhatsAppClick = (number, order) => {
    if (!number) {
      toast.error('Phone number not available');
      return;
    }
    shareWhatsAppToNumber(number, buildOrderWhatsAppMessage(order));
  };

  const caseLabelFor = (order) =>
    STATUS_LABELS[order.status]
    || (order.refundTimeline?.daysLeftToClose != null
      ? `Refund closes in ${order.refundTimeline.daysLeftToClose} day(s)`
      : 'Needs attention');

  return (
    <div style={{ padding: '24px', flex: 1, background: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', fontWeight: 800, color: '#1e293b' }}>
              Action Required
            </h1>
            <p style={{ margin: 0, color: '#64748b' }}>
              Rejected, refill, reject-and-refill, and refund forms closing soon
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={() => navigate('/admin/orders')}>All Orders</Button>
            <Button onClick={fetchActionRequiredOrders} loading={loading} icon="refresh">Refresh</Button>
          </div>
        </div>

        <div style={{
          marginBottom: '16px',
          padding: '16px 18px',
          borderRadius: '14px',
          background: '#fdf2f8',
          border: '1px solid #f9a8d4',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 800, color: '#9d174d' }}>
            {loading ? 'Loading…' : `${orders.length} order(s) need attention`}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading action-required orders…</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No action-required orders right now</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {orders.map((order) => (
                <div
                  key={order.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                    padding: '14px 18px',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ minWidth: '140px' }}>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>{order.orderId}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{order.customer}</div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background: '#fce7f3',
                    color: '#9d174d',
                  }}>
                    {caseLabelFor(order)}
                  </span>
                  <div style={{ flex: 1, minWidth: '180px', fontSize: '13px', color: '#475569' }}>
                    <div>{order.product}</div>
                    {order.reason && <div style={{ marginTop: '4px', color: '#9d174d' }}>Reason: {order.reason}</div>}
                    {order.mediatorName && <div style={{ marginTop: '2px' }}>Mediator: {order.mediatorName}</div>}
                  </div>
                  <button
                    onClick={() => handleWhatsAppClick(order.whatsappNumber, order)}
                    style={{
                      padding: '8px 12px',
                      background: '#25D366',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    WhatsApp Buyer
                  </button>
                  <button
                    onClick={() => navigate('/admin/orders', { state: { focusOrderId: order.orderId } })}
                    style={{
                      padding: '8px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Icon name="eye" size={14} />
                    Open in Orders
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActionRequired;
